import { f7, useStore } from 'framework7-react';
import { FC } from 'react';
import style from './RegisterNewMemberConfirmStyle.module.css';
import { postInspectionCertifications } from '@/api/registerQRApi';
import { Button } from '@/components/uiParts/Button';
import { RegisterQRPageTemplate } from '@/components/uiParts/Template/RegisterQRPageTemplate/RegisterQRPageTemplate';
import { isApp } from '@/config/device';
import { RegisterUserDataType } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { mobileAuthSelectedProcess } from '@/consts/auth';
import { useMobileAuth } from '@/hooks/api/auth/useMobileAuth';
import { useToast } from '@/hooks/useToast';
import { MobileAuthApiParams } from '@/types/api/authApi';
import { convertSlashToHyphen, hyphenDate } from '@/utils/date';
import { compressImageToJpeg } from '@/utils/imageCompression';
import { storeDispatch } from '@/utils/store';

export const RegisterNewMemberConfirm: FC = () => {
  const registerUserData = useStore(
    store,
    'getRegisterUser',
  ) as RegisterUserDataType;
  const { isCorporation, scan } = registerUserData;
  const { openToast } = useToast();
  const { authenticateMobile } = useMobileAuth();

  const vehicleNo = [
    scan.transport_bu_name,
    scan.class_no,
    scan.hiragana,
    scan.hiragana && scan.registry_no && '・',
    scan.registry_no,
  ]
    .filter(Boolean)
    .join(' ');

  window.onInspectionCertificateScan = (data) => {
    try {
      const parsed: {
        transport_bu_name: string;
        class_no: string;
        hiragana: string;
        registry_no: string;
        registry_no_erased?: number;
        businesswork_flg?: number;
        inspection_certificate_file: string | File | Blob; // Base64 image if necessary
        vin: string;
        motor_model?: string;
        katasiki_num?: number;
        ruibetsu_num?: number;
        inspection_limit: string; // YYYY/MM/DD
        model_code: string;
        first_registration: string; // YYYY-MM-DD
      } = JSON.parse(data);
      storeDispatch('setRegisterUser', 'registerUser', {
        ...registerUserData,
        scan: {
          ...parsed,
          next_inspection_date: parsed.inspection_limit,
          registration_date: parsed.first_registration,
        },
      });
      f7.view.main.router.navigate(paths.registerNewMemberConfirm);
    } catch {}
  };

  window.onInspectionCertificateCapture = (imageBase64) => {
    storeDispatch('setRegisterUser', 'registerUser', {
      ...registerUserData,
      scan: {
        ...registerUserData.scan,
        inspection_certificate_file: imageBase64,
      },
    });
  };

  const openInspectionCertificateScan = (isReScan: boolean) => {
    if (isApp) {
      if (isReScan) {
        window.location.href = '/inspection_certificate_reader';
      } else {
        window.location.href = '/inspection_certificate_capture';
      }
    }
  };

  // ユーティリティ: data URL → { mime, blob }
  const dataUrlToBlob = (dataUrl: string) => {
    const m = dataUrl.match(/^data:(.*?);base64,(.*)$/);
    if (!m) throw new Error('Invalid data URL');
    const mime = m[1] || 'application/octet-stream';
    const b64 = m[2];

    const hasAtob =
      typeof globalThis !== 'undefined' &&
      typeof (globalThis as any).atob === 'function';
    const binary = hasAtob
      ? (globalThis as any).atob(b64)
      : Buffer.from(b64, 'base64').toString('binary');
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: mime });
    return { mime, blob };
  };

  const handleSubmit = async () => {
    f7.preloader.show();
    try {
      const certificateParams = new FormData();

      if (
        registerUserData.m_customer_id ||
        store.state.authInfo.m_customer_id
      ) {
        certificateParams.append(
          'm_customer_id',
          registerUserData.m_customer_id?.toString() ||
            store.state.authInfo.m_customer_id.toString(),
        );
      }
      if (registerUserData.affiliate_id) {
        certificateParams.append(
          'm_affiliate_id',
          registerUserData.affiliate_id.toString(),
        );
      }
      certificateParams.append(
        'transport_bu_name',
        scan.transport_bu_name || '',
      );
      certificateParams.append('class_no', scan.class_no || '');
      certificateParams.append('hiragana', scan.hiragana || '');
      certificateParams.append('registry_no', scan.registry_no || '');
      certificateParams.append('registry_no_erased', '0');
      certificateParams.append('businesswork_flg', '0');

      // 車検証アップロード data URL または Blob を圧縮して送る
      const v = scan.inspection_certificate_file as
        | string
        | Blob
        | File
        | undefined;
      if (v) {
        const HARD_LIMIT_BYTES = 8 * 1024 * 1024; // 8MB
        const TARGET_BYTES = 3.5 * 1024 * 1024; // 3.5MB 目標
        try {
          // 元データ（フォールバック用）
          const original =
            typeof v === 'string'
              ? dataUrlToBlob(v)
              : {
                  mime: (v as any).type || 'application/octet-stream',
                  blob: v as Blob,
                };

          // 画像圧縮（2000px・JPEG・品質0.85→0.5）
          const { blob: compressedBlob } = await compressImageToJpeg(v, {
            maxDimension: 3800,
            mime: 'image/jpeg',
            initialQuality: 0.9,
            minQuality: 0.8,
            qualityStep: 0.03,
            targetBytes: TARGET_BYTES,
          });

          if (compressedBlob.size > HARD_LIMIT_BYTES) {
            // 8MB を超える場合は送信中止
            f7.preloader.hide();
            f7.dialog.alert(
              '車検証の画像ファイルは8MB以下にしてください。解像度を下げて再撮影するか、別の画像をお試しください。',
              'ファイルサイズが大きすぎます',
            );
            return; // 送信中止
          }

          // React Native の FormData では第3引数の fileName を付けるのが安定
          (certificateParams as any).append(
            'inspection_certificate_file',
            compressedBlob as any,
            'certificate.jpg',
          );
        } catch (error) {
          console.error(
            'Failed to compress inspection certificate file:',
            error,
          );
          // 圧縮失敗時はフォールバック: 元Blobのサイズで判断
          try {
            const fallback =
              typeof v === 'string'
                ? dataUrlToBlob(v)
                : {
                    mime: (v as any).type || 'application/octet-stream',
                    blob: v as Blob,
                  };
            if (fallback.blob.size > HARD_LIMIT_BYTES) {
              f7.preloader.hide();
              f7.dialog.alert(
                '車検証の画像ファイルは8MB以下にしてください。解像度を下げて再撮影するか、別の画像をお試しください。',
                'ファイルサイズが大きすぎます',
              );
              return; // 送信中止
            }
            (certificateParams as any).append(
              'inspection_certificate_file',
              fallback.blob as any,
              `certificate.${fallback.mime.split('/')[1] || 'bin'}`,
            );
          } catch (e) {
            console.error('Failed to fallback with original blob:', e);
            f7.preloader.hide();
            f7.dialog.alert(
              '画像の処理に失敗しました。別の画像でお試しください。',
              'エラー',
            );
            return;
          }
        }
      }

      certificateParams.append('vin', scan.vin || '');
      certificateParams.append('motor_model', scan.motor_model || '');
      certificateParams.append('katasiki_num', scan.katashiki_num || '');
      certificateParams.append('ruibetsu_num', scan.ruibetsu_kbn || '');
      certificateParams.append(
        'next_inspection_date',
        convertSlashToHyphen(scan.next_inspection_date),
      );
      certificateParams.append('model_code', scan.model_code || '');
      certificateParams.append(
        'registration_date',
        scan.registration_date || '',
      );

      const certResponse = await postInspectionCertifications(
        certificateParams,
      );

      if (!certResponse?.data) {
        throw new Error(
          'サーバーから応答データがありません。時間を置いてから再度お試しください。',
        );
      }
      if (!certResponse.data.success) {
        throw new Error(
          certResponse.data.error?.message || '車検証の登録に失敗しました。',
        );
      }

      openToast('車検証登録が完了しました。', 'toast-success');

      // ログイン済みユーザーの車両追加（ホームから来た場合）はSMS認証不要
      const isLoggedIn = !!store.state.authInfo.m_customer_id;
      if (isLoggedIn) {
        f7.view.main.router.navigate(paths.home, { reloadAll: true });
      } else {
        // 新規会員登録フローの場合のみSMS認証コードを送信
        const mobileAuthParams: MobileAuthApiParams = {
          phone_no:
            registerUserData.step1.tel1 +
            '-' +
            registerUserData.step1.tel2 +
            '-' +
            registerUserData.step1.tel3,
          selected_process: mobileAuthSelectedProcess.login,
          m_customer_id:
            registerUserData.m_customer_id ||
            store.state.authInfo.m_customer_id,
          m_affiliate_id: registerUserData.affiliate_id
            ? String(registerUserData.affiliate_id)
            : undefined,
        };
        await authenticateMobile(mobileAuthParams);
      }
    } catch (e: any) {
      f7.dialog.alert(e?.message || 'エラーが発生しました。', 'エラー');
      throw e;
    }
    f7.preloader.hide();
  };

  return (
    <RegisterQRPageTemplate
      pageName="RegisterConfirm"
      title="入力内容の確認"
      showNavBack
      confirmPage
      handleClickBack={() => f7.view.main.router.back()}
    >
      <div className={style['page']}>
        <div className={`${style['block']}`}>
          <div className={style['block-header']}>
            <div className={style['flex-center']}>
              <div className={style['title']}>車両情報</div>
              <div
                className={style['edit-btn']}
                onClick={() => openInspectionCertificateScan(true)}
              >
                編集
              </div>
            </div>
            {!scan.inspection_certificate_file && (
              <p className={style['section-description']}>
                ※ 車両情報は後からでも登録できます
              </p>
            )}

            <div
              className={`${style['content-line']} ${style['border-bottom-normal']}`}
            >
              <p className={style['text-label']}>車検証の写真</p>
              <div className={style['inspection-image']}>
                {scan.inspection_certificate_file && (
                  <img
                    src={scan.inspection_certificate_file}
                    style={{
                      borderRadius: 8,
                    }}
                    alt="inspection_certificate_file"
                  />
                )}
                <Button
                  className={style['reshoot-btn']}
                  onClick={() => openInspectionCertificateScan(!vehicleNo)}
                >
                  {scan.inspection_certificate_file
                    ? '再撮影'
                    : '車検証を撮影する'}
                </Button>
              </div>
            </div>
          </div>

          <div className={`${style['block-header']} ${style['vehicle-cert']}`}>
            <div className={style['title']}>車検証情報</div>
            {vehicleNo && (
              <div className={style['vehicle-cert-info']}>
                <p> {`${scan.transport_bu_name || ''} ${scan.class_no}`}</p>
                <p className={style['vehicle-cert-info__name']}>
                  {scan.hiragana}・
                  <span className={style['bold']}>{scan.registry_no}</span>
                </p>
              </div>
            )}
          </div>
          <div className={style['divide']}>
            <div className={style['content-line-car-info']}>
              <p className={style['text-label-medium']}>車両ナンバー：</p>
              <p className={style['text']}>{vehicleNo || '-'}</p>
            </div>
            <div className={style['content-line-car-info']}>
              <p className={style['text-label-medium']}>登録年月日：</p>
              <p className={style['text']}>
                {scan.registration_date
                  ? hyphenDate(scan.registration_date)
                  : '-'}
              </p>
            </div>
            <div className={style['content-line-car-info']}>
              <p className={style['text-label-medium']}>車検満了日：</p>
              <p className={style['text']}>
                {scan.next_inspection_date
                  ? hyphenDate(scan.next_inspection_date)
                  : '-'}
              </p>
            </div>
            <div className={style['content-line-car-info']}>
              <p className={style['text-label-medium']}>車台番号：</p>
              <p className={style['text']}>{scan.vin || '-'}</p>
            </div>
            <div className={style['content-line-car-info']}>
              <p className={style['text-label-medium']}>型式：</p>
              <p className={style['text']}>{scan.model_code || '-'}</p>
            </div>
          </div>
        </div>
        <Button onClick={handleSubmit}>車検証登録を完了</Button>
      </div>
    </RegisterQRPageTemplate>
  );
};
